import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Loader2, ArrowLeft } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const resolveUrl = (url) => {
  if (!url) return '';
  return url.startsWith('http') ? url : `${API_URL}${url}`;
};

const ExperienceTile = ({ exp, index }) => {
  const navigate = useNavigate();
  const [imgLoaded, setImgLoaded] = useState(false);
  const bgUrl = resolveUrl(exp.background_url);

  return (
    <button
      onClick={() => navigate(`/the-show/${exp.id}?from=showroom`)}
      className="group relative w-full aspect-[16/10] rounded-2xl overflow-hidden cursor-pointer focus:outline-none focus:ring-2 focus:ring-[#0077FF]/50 focus:ring-offset-2 focus:ring-offset-[#060a14]"
      style={{
        animationDelay: `${0.15 + index * 0.1}s`,
        animation: 'showroom-tile-in 0.6s ease-out both',
      }}
      data-testid={`preview-tile-${exp.id}`}
    >
      {bgUrl && (
        <img src={bgUrl} alt={exp.name}
          className={`absolute inset-0 w-full h-full object-cover transition-all duration-700 ease-out
            ${imgLoaded ? 'opacity-100' : 'opacity-0'}
            group-hover:scale-[1.06] group-focus:scale-[1.06]`}
          onLoad={() => setImgLoaded(true)} draggable={false} />
      )}
      {!imgLoaded && <div className="absolute inset-0 bg-white/[0.03]" />}

      <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/20 to-transparent
        group-hover:from-black/70 group-hover:via-black/10 transition-all duration-500" />

      <div className="absolute inset-0 border border-white/[0.06] rounded-2xl
        group-hover:border-[#0077FF]/30 transition-colors duration-400" />

      <div className="absolute bottom-0 left-0 right-0 p-5 sm:p-6">
        <h3 className="text-lg sm:text-xl font-bold text-white tracking-tight
          group-hover:text-[#4DB8FF] transition-colors duration-300">
          {exp.name}
        </h3>
        {exp.vertical && (
          <p className="text-[11px] text-white/35 mt-1 uppercase tracking-[0.15em] font-medium
            group-hover:text-white/50 transition-colors duration-300">
            {exp.vertical}
          </p>
        )}
      </div>

      <div className="absolute top-4 right-4 opacity-0 group-hover:opacity-100
        transition-all duration-300 translate-x-1 group-hover:translate-x-0">
        <div className="w-8 h-8 rounded-full bg-[#0077FF]/20 border border-[#0077FF]/30
          flex items-center justify-center backdrop-blur-sm">
          <svg className="w-3.5 h-3.5 text-[#4DB8FF]" viewBox="0 0 20 20" fill="currentColor">
            <path fillRule="evenodd" d="M3 10a.75.75 0 01.75-.75h10.638L10.23 5.29a.75.75 0 111.04-1.08l5.5 5.25a.75.75 0 010 1.08l-5.5 5.25a.75.75 0 11-1.04-1.08l4.158-3.96H3.75A.75.75 0 013 10z" clipRule="evenodd" />
          </svg>
        </div>
      </div>
    </button>
  );
};

const AdminShowroomPreview = () => {
  const { showroomId } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [showroomName, setShowroomName] = useState('');
  const [experiences, setExperiences] = useState([]);
  const [error, setError] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const res = await fetch(`${API_URL}/api/showrooms/${showroomId}/preview`, { credentials: 'include' });
        if (res.status === 401 || res.status === 403) {
          navigate('/admin/login?redirect=/admin/showrooms', { replace: true });
          return;
        }
        if (!res.ok) throw new Error('Failed to load showroom');
        const data = await res.json();
        setShowroomName(data.showroom?.name || 'Showroom');
        setExperiences(data.experiences || []);
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [showroomId, navigate]);

  if (loading) {
    return (
      <div className="fixed inset-0 bg-[#060a14] flex items-center justify-center" data-testid="showroom-preview-loading">
        <Loader2 className="w-8 h-8 text-[#0077FF] animate-spin" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="fixed inset-0 bg-[#060a14] flex items-center justify-center px-4" data-testid="showroom-preview-error">
        <div className="text-center">
          <p className="text-white/50 text-sm mb-4">{error}</p>
          <button onClick={() => navigate('/admin/showrooms')}
            className="text-[#0077FF] text-sm hover:underline">Back to Showrooms</button>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-[#060a14] flex flex-col" data-testid="showroom-preview-page">
      <style>{`
        @keyframes showroom-tile-in {
          0% { opacity: 0; transform: translateY(20px) scale(0.97); }
          100% { opacity: 1; transform: translateY(0) scale(1); }
        }
      `}</style>

      {/* Top bar */}
      <div className="flex items-center justify-between px-4 sm:px-6 py-3 border-b border-white/[0.06]">
        <div className="flex items-center gap-3">
          <button onClick={() => navigate('/admin/showrooms')} data-testid="preview-back-btn"
            className="w-8 h-8 rounded-full bg-white/[0.06] hover:bg-white/[0.1] flex items-center justify-center transition-colors">
            <ArrowLeft className="w-4 h-4 text-white/50" />
          </button>
          <div>
            <h1 className="text-sm font-bold text-white tracking-tight">{showroomName}</h1>
            <p className="text-[10px] text-white/30 mt-0.5 uppercase tracking-[0.12em]">
              Admin Preview &middot; {experiences.length} experience{experiences.length !== 1 ? 's' : ''}
            </p>
          </div>
        </div>
        <div className="px-2.5 py-1 rounded-full bg-emerald-500/10 border border-emerald-500/20">
          <span className="text-[10px] font-semibold text-emerald-400 uppercase tracking-wider">Admin Preview</span>
        </div>
      </div>

      {/* Gallery grid */}
      <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-6">
        {experiences.length === 0 ? (
          <div className="flex items-center justify-center h-full">
            <p className="text-white/30 text-sm">No experiences assigned to this showroom</p>
          </div>
        ) : (
          <div className="max-w-5xl mx-auto grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5">
            {experiences.map((exp, i) => (
              <ExperienceTile key={exp.id} exp={exp} index={i} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminShowroomPreview;
